// eslint-disable-next-line @heidi/rules/no-new-direct-tauri-import -- Dictate main-window navigation event adapter, SCR-7613
import { listen } from '@tauri-apps/api/event';
// eslint-disable-next-line @heidi/rules/no-new-direct-tauri-import -- Dictate main-window navigation host guard, SCR-7613
import { getCurrentWindow } from '@tauri-apps/api/window';
import { useRouter } from 'next/router';
import { useEffect } from 'react';

import {
  DICTATION_OPEN_SIGN_IN_EVENT,
  DICTATION_SIGN_IN_STEP,
  getDictationSignInHref,
} from '@/composition/hosts/dictation/dictation-widget/openDictationSignIn';
import { patchPersistedOnboardingState } from '@/features/dictation/dictate-onboarding-flow/utils/dictateOnboardingPersistence';
import { listenWidgetOpenFeedbackHistory } from '@/tauri/widget/api';
import { logger } from '@/utils/logger/logger';
import { DICTATION_FEEDBACK_HISTORY_HREF } from '@heidi/dictate/contracts';
import { MAIN_WINDOW_LABEL } from '@heidi/plugin-core';
import { sanitizeError } from '@heidi/runtime/logger';
import { isTauri } from '@heidi/runtime/platform';

/** Routes native Dictate navigation events received by the main desktop window. */
export function useMainWindowDictationNavigationListener(): void {
  const router = useRouter();

  useEffect(() => {
    if (!isTauri() || getCurrentWindow().label !== MAIN_WINDOW_LABEL) return;

    let cancelled = false;
    const unlistenPromise = listen<void>(DICTATION_OPEN_SIGN_IN_EVENT, () => {
      if (cancelled) return;
      void (async () => {
        try {
          await patchPersistedOnboardingState({ currentStep: DICTATION_SIGN_IN_STEP });
        } catch (error) {
          logger.warn(
            '[dictation/open-sign-in] persist failed, proceeding with navigation',
            { error: error instanceof Error ? error.message : String(error) }
          );
        }
        await router.push(getDictationSignInHref());
      })();
    });

    return () => {
      cancelled = true;
      void unlistenPromise
        .then((unlisten) => unlisten())
        .catch((error: unknown) => {
          logger.debug('[dictation/open-sign-in] listener cleanup failed', {
            error: error instanceof Error ? error.message : String(error),
          });
        });
    };
  }, [router]);

  useEffect(() => {
    if (!isTauri() || getCurrentWindow().label !== MAIN_WINDOW_LABEL) return;

    let cancelled = false;
    const unlistenPromise = listenWidgetOpenFeedbackHistory(() => {
      if (cancelled) return;
      void router.push(DICTATION_FEEDBACK_HISTORY_HREF);
    });

    return () => {
      cancelled = true;
      void unlistenPromise
        .then((unlisten) => unlisten())
        .catch((error: unknown) => {
          logger.debug('[dictation/feedback-history] listener cleanup failed', {
            error: sanitizeError(error),
          });
        });
    };
  }, [router]);
}
